'use client';

import React from 'react';

interface PostMeetingFeedbackModalProps {
  open: boolean;
  formUrl: string;
  onClose: () => void;
}

export function PostMeetingFeedbackModal({
  open,
  formUrl,
  onClose,
}: PostMeetingFeedbackModalProps) {
  const [iframeLoaded, setIframeLoaded] = React.useState(false);
  const [showLoadFallback, setShowLoadFallback] = React.useState(false);

  React.useEffect(() => {
    if (!open) {
      return;
    }

    setIframeLoaded(false);
    setShowLoadFallback(false);

    const timeout = window.setTimeout(() => {
      setShowLoadFallback(true);
    }, 4000);

    return () => {
      window.clearTimeout(timeout);
    };
  }, [open]);

  React.useEffect(() => {
    if (!open) {
      return;
    }

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = originalOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, onClose]);

  const handleOpenInNewTab = React.useCallback(() => {
    window.open(formUrl, '_blank', 'noopener,noreferrer');
  }, [formUrl]);

  if (!open) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/70 backdrop-blur-sm">
      <div className="relative flex h-screen w-screen flex-col overflow-hidden bg-white outline-none dark:bg-neutral-900">
        <div className="relative flex-1 overflow-hidden bg-neutral-50 dark:bg-neutral-950">
          <iframe
            title="Formulário de feedback da reunião"
            src={formUrl}
            className="h-full w-full border-none"
            onLoad={() => setIframeLoaded(true)}
          />
          {!iframeLoaded && (
            <div className="absolute inset-0 flex items-center justify-center bg-white/90 text-neutral-600 dark:bg-neutral-900/90 dark:text-neutral-300">
              Carregando formulário...
            </div>
          )}
        </div>

        {showLoadFallback && !iframeLoaded ? (
          <div className="absolute bottom-6 left-1/2 z-10 flex w-[min(480px,90%)] -translate-x-1/2 items-center justify-between rounded-full bg-black/70 px-4 py-2 text-sm text-white shadow-lg backdrop-blur">
            <span>Parece que está demorando. Tente abrir em uma nova aba.</span>
            <button
              type="button"
              onClick={handleOpenInNewTab}
              className="rounded-full bg-white px-3 py-1 text-xs font-semibold text-neutral-900 transition hover:bg-neutral-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
            >
              Abrir
            </button>
          </div>
        ) : (
          <div className="pointer-events-none absolute bottom-6 right-6 z-10"></div>
        )}
      </div>
    </div>
  );
}
